<script setup lang="ts">
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import VueMarkdown from "vue-markdown-render";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { Color, Size } from "@/abc-dsk/options";
import ExamPreparationItem from "@/components/examPreparation/ExamPreparationItem.vue";
import ImageResponsive from "@/components/ImageResponsive.vue";

const { rt, t, tm } = useI18n();

interface AdviceBox {
  title: string;
  content: string;
  positionClasses: string[];
}

const adviceBoxes = computed<AdviceBox[]>(() => {
  const boxes = tm("examPreparation.generalAdvice.boxes") as unknown as {
    title: string;
    content: string;
  }[];

  return boxes.map((box, index) => {
    const { title, content } = box;

    const positionClasses = ["col-12 col-lg-8"];
    if (index % 2 === 1) {
      positionClasses.push("offset-lg-4");
    }

    return {
      title: `${index + 1}. ${rt(title)}`,
      content: rt(content),
      positionClasses
    };
  });
});
</script>

<template>
  <main class="exam-preparation">
    <section
      class="exam-preparation__section exam-preparation__section--secondary-bg container-fluid pb-1 text-center"
    >
      <div class="col-12">
        <h1 class="exam-preparation__heading exam-preparation__heading--primary">
          {{ t("examPreparation.main.headline") }}
        </h1>
        <AbcHeading
          class="exam-preparation__heading exam-preparation__heading--secondary"
          tag="h2"
          size="xl"
        >
          {{ t("examPreparation.main.secondaryHeadline") }}
        </AbcHeading>
        <p>
          {{ t("examPreparation.main.lead") }}
        </p>
      </div>
    </section>

    <section
      class="exam-preparation__section exam-preparation__section--gradient-exit container-fluid"
    >
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-6 mb-5">
            <AbcHeading tag="h2" size="lg" class="exam-preparation__heading mb-3">
              {{ t("examPreparation.op5.headline") }}
            </AbcHeading>
            <p>{{ t("examPreparation.op5.lead") }}</p>
            <div class="exam-preparation__op5-buttons">
              <TgButton
                tag="a"
                href="https://apps.apple.com/us/app/ordbogsprogrammet-5/id1599705846"
                target="_blank"
                class="mb-3 mb-md-0 exam-preparation__op5-button exam-preparation__op5-button--first"
              >
                {{ t("examPreparation.op5.downloadMac") }}
              </TgButton>
              <TgButton
                tag="a"
                href="https://www.microsoft.com/store/apps/9MWJCH57DXVG"
                target="_blank"
                class="exam-preparation__op5-button"
                >{{ t("examPreparation.op5.downloadWindows") }}</TgButton
              >
            </div>
          </div>
          <div class="col-12 col-lg-5 offset-lg-1 d-flex justify-content-center">
            <ImageResponsive
              presentational
              src="/assets/img/exam-preparation/op5-children@1x.png"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="exam-preparation__section">
      <div class="container">
        <div class="row d-lg-flex align-items-center">
          <div class="d-none d-lg-block col-5">
            <ImageResponsive
              class="exam-preparation__accessibility-image"
              presentational
              src="/assets/img/about/children-with-devices@1x.png"
            />
          </div>
          <div class="col-12 col-lg-6 offset-lg-1">
            <AbcHeading tag="h3" size="lg" class="exam-preparation__heading mb-3">
              {{ t("examPreparation.accessibility.headline") }}
            </AbcHeading>
            <p><VueMarkdown :source="t('examPreparation.accessibility.text-1-markdown')" /></p>
            <p><VueMarkdown :source="t('examPreparation.accessibility.text-2-markdown')" /></p>
            <p>{{ t("examPreparation.accessibility.text-3") }}</p>
          </div>
          <div class="d-flex d-lg-none justify-content-center col-12 mt-5">
            <ImageResponsive
              img-class="exam-preparation__accessibility-image exam-preparation__accessibility-image--small"
              presentational
              src="/assets/img/about/children-with-devices@1x.png"
            />
          </div>
        </div>
      </div>
    </section>

    <section
      class="exam-preparation__section exam-preparation__section--gradient-enter container-fluid"
    >
      <div class="container">
        <div class="row">
          <div class="col-12 text-center">
            <AbcHeading class="exam-preparation__heading" tag="h3" size="lg">
              {{ t("examPreparation.generalAdvice.headline") }}
            </AbcHeading>
          </div>
        </div>
        <div v-for="(box, index) in adviceBoxes" :key="index" class="row my-5">
          <div :class="box.positionClasses">
            <ExamPreparationItem>
              <template #title>
                <AbcHeading class="exam-preparation__advice-box-title" tag="h4" size="sm">
                  {{ box.title }}
                </AbcHeading>
              </template>
              <template #content>
                <p>{{ box.content }}</p>
              </template>
            </ExamPreparationItem>
          </div>
        </div>
      </div>
    </section>

    <section
      class="exam-preparation__section exam-preparation__section--secondary-bg container-fluid"
    >
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-6">
            <AbcHeading tag="h3" size="lg" class="exam-preparation__heading mb-3">
              {{ t("examPreparation.grammatip.headline") }}
            </AbcHeading>
            <p>{{ t("examPreparation.grammatip.text") }}</p>

            <TgButton
              tag="a"
              href="https://www.grammatip.com/student/free-exercises"
              target="_blank"
              class="mt-3"
              :color="Color.Primary"
              :size="Size.Lg"
            >
              {{ t("examPreparation.grammatip.button") }}
            </TgButton>
          </div>
          <div
            class="col-12 col-lg-6 d-flex justify-content-center d-lg-block exam-preparation__bubber"
          >
            <ImageResponsive presentational src="/assets/img/exam-preparation/bubber@1x.png" />
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.exam-preparation {
  background: white;

  &__section {
    padding: 64px 0;

    &--secondary-bg {
      background: rgb(@theme-colors[@secondary][10]);
    }

    &--gradient-enter {
      background: linear-gradient(180deg, white 35%, rgb(@theme-colors[@secondary][10]));
    }

    &--gradient-exit {
      background: linear-gradient(180deg, rgb(@theme-colors[@secondary][10]) 85%, white);
    }
  }

  &__heading {
    &--primary {
      color: rgb(@theme-colors[@primary][base]);
      font-size: 1.125rem;
    }

    &--secondary {
      margin-bottom: 13px;
    }
  }

  &__op5-buttons {
    text-align: center;

    #media-breakpoint-up(sm, {
      text-align: left;
    });
  }

  &__op5-button {
    margin: 0 10px;

    #media-breakpoint-up(sm, {
      margin: 0;

      &--first {
        margin-right: 10px;
      }
    });
  }

  ::v-deep(&__accessibility-image--small) {
    max-height: 250px;
  }

  &__advice-box-title {
    color: white;
  }

  &__grammatip-button {
    margin-top: -64px;
    padding-top: 160px;
    text-align: center;
  }

  &__bubber {
    margin-top: 48px;

    #media-breakpoint-up(lg, {
      margin-top: -70px;
    });
  }
}
</style>
